import { Skeleton, SkeletonTypes, HStack, VStack, StackTypes } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import React from 'react';

export interface AssetsLoadingProps {
  quantity?: number;
  ProductItemLoading: React.FC;
}

export interface TextSkeletonProps {
  hasCaptions?: boolean;
  w?: StackTypes.Props['w'];
  h?: StackTypes.Props['h'];
}

export interface TextWithSubtextSkeletonProps {
  w?: StackTypes.Props['w'];
  subtextW?: StackTypes.Props['w'];
}

export interface ButtonSkeletonProps {
  borderRadius?: StackTypes.Props['borderRadius'];
}

export const ColoredSkeleton = (props: SkeletonTypes.Props) => (
  <Skeleton {...props} endColor="brand.primary.lightest" />
);

export const AssetsLoading: React.FC<AssetsLoadingProps> = ({
  ProductItemLoading,
  quantity = 1,
}) => {
  return (
    <VStack space={Spacing['stack.nano']} my={Spacing['stack.nano']}>
      {[...Array(quantity)].map((_, index) => (
        <ProductItemLoading key={index} />
      ))}
    </VStack>
  );
};

export const CaptionSkeleton: React.FC = () => {
  return (
    <ColoredSkeleton
      width={Typography['fontsize.xs']}
      height={Typography['fontsize.xs']}
      borderRadius={BoxRadius.circular}
    />
  );
};

export const TextSkeleton: React.FC<TextSkeletonProps> = ({
  hasCaptions,
  w = '80%',
  h = Typography['fontsize.md'],
}) => {
  const skeleton = <ColoredSkeleton width={w} height={h} borderRadius={BoxRadius.sm} />;

  if (hasCaptions) {
    return (
      <VStack space={Spacing['stack.quarck']} w={w}>
        {skeleton}
        <HStack space={Spacing['inline.quarck']}>
          {[...Array(3)].map((_, index) => (
            <CaptionSkeleton key={index} />
          ))}
        </HStack>
      </VStack>
    );
  }

  return skeleton;
};

export const DoubleTextSkeleton: React.FC<TextSkeletonProps> = ({ w = '80%' }) => {
  const skeleton = (
    <ColoredSkeleton width={w} height={Typography['fontsize.md']} borderRadius={BoxRadius.sm} />
  );

  return (
    <VStack space={Spacing['stack.quarck']} w={w}>
      {skeleton}
      {skeleton}
    </VStack>
  );
};

export const TextWithSubtextSkeleton: React.FC<TextWithSubtextSkeletonProps> = ({
  w = '80%',
  subtextW = '60%',
}) => {
  return (
    <VStack space={Spacing['stack.quarck']} w={w}>
      <ColoredSkeleton width={w} height={Typography['fontsize.md']} borderRadius={BoxRadius.sm} />
      <ColoredSkeleton
        width={subtextW}
        height={Typography['fontsize.xs']}
        borderRadius={BoxRadius.sm}
      />
    </VStack>
  );
};

export const DoubleHorizontalTextSkeleton: React.FC<TextSkeletonProps> = ({ w = '35%' }) => {
  const skeleton = (
    <ColoredSkeleton width={w} height={Typography['fontsize.md']} borderRadius={BoxRadius.sm} />
  );

  return (
    <HStack space={Spacing['stack.quarck']} justifyContent="space-between" w="full">
      {skeleton}
      {skeleton}
    </HStack>
  );
};

export const ImageSkeleton: React.FC = () => {
  return <ColoredSkeleton width="54px" height="48px" borderRadius={BoxRadius.sm} />;
};

export const DetailsSkeleton: React.FC = () => {
  return <ColoredSkeleton width="24px" height="24px" borderRadius={BoxRadius.circular} />;
};

export const InputSkeleton: React.FC = () => {
  return <ColoredSkeleton width="full" height="40px" borderRadius={BoxRadius.sm} />;
};

export const ButtonSkeleton: React.FC<ButtonSkeletonProps> = ({ borderRadius = BoxRadius.lg }) => {
  return <ColoredSkeleton width="full" height="40px" borderRadius={borderRadius} />;
};

export const RiskSkeleton: React.FC = () => {
  return (
    <HStack space={Spacing['inline.quarck']}>
      {[...Array(5)].map((_, index) => (
        <ColoredSkeleton
          key={index}
          width={Spacing['inline.xxxs']}
          height={Spacing['inline.xxxs']}
          borderRadius={BoxRadius.circular}
        />
      ))}
    </HStack>
  );
};

export const StatusSkeleton: React.FC<TextSkeletonProps> = ({
  w = '60%',
  h = Typography['fontsize.md'],
}) => {
  return (
    <HStack w="full" alignItems="center" space="inline.quarck">
      <ColoredSkeleton width="inline.xxs" height="stack.xxs" borderRadius={BoxRadius.circular} />
      <ColoredSkeleton width={w} height={h} borderRadius={BoxRadius.sm} />
    </HStack>
  );
};
